import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: "DE", name: "Deutschland" },
  { code: "AT", name: "Österreich" },
  { code: "CH", name: "Schweiz" },
  { code: "LU", name: "Luxemburg" },
  { code: "NL", name: "Niederlande" },
];

/**
 * Modal zum Erfassen/Bearbeiten der Rechnungsadresse. Wird VOR jedem
 * Vollversion-Checkout aufgerufen, wenn die Adresse noch nicht komplett ist.
 * Nach erfolgreichem Speichern ruft es `onSaved(data)` auf — der Aufrufer
 * kann darauf den Stripe-Checkout starten.
 */
export default function BillingAddressDialog({ open, onOpenChange, onSaved, initial = null }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", line1: "", line2: "",
    postal_code: "", city: "", country: "DE",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initial) setForm({ ...form, ...initial });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()
        || !form.line1.trim() || !form.postal_code.trim() || !form.city.trim()) {
      toast.error("Bitte fülle alle Pflichtfelder aus.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/billing/billing-details`, form, { withCredentials: true });
      toast.success("Rechnungsadresse gespeichert");
      onSaved?.(r.data);
    } catch (err) {
      toast.error("Speichern fehlgeschlagen", { description: err?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="billing-address-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Rechnungsadresse</DialogTitle>
          <DialogDescription>
            Nötig für deine Rechnung. Wir übernehmen die Angaben direkt an Stripe.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ba-first" className="text-xs uppercase tracking-wider text-muted-foreground">Vorname</Label>
              <Input id="ba-first" value={form.first_name} onChange={set("first_name")} className="mt-1" data-testid="ba-first-name" autoFocus />
            </div>
            <div>
              <Label htmlFor="ba-last" className="text-xs uppercase tracking-wider text-muted-foreground">Nachname</Label>
              <Input id="ba-last" value={form.last_name} onChange={set("last_name")} className="mt-1" data-testid="ba-last-name" />
            </div>
          </div>
          <div>
            <Label htmlFor="ba-line1" className="text-xs uppercase tracking-wider text-muted-foreground">Straße und Hausnummer</Label>
            <Input id="ba-line1" value={form.line1} onChange={set("line1")} className="mt-1" data-testid="ba-line1" placeholder="Musterstraße 12" />
          </div>
          <div>
            <Label htmlFor="ba-line2" className="text-xs uppercase tracking-wider text-muted-foreground">Adresszusatz (optional)</Label>
            <Input id="ba-line2" value={form.line2} onChange={set("line2")} className="mt-1" data-testid="ba-line2" placeholder="c/o, 2. OG …" />
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <div>
              <Label htmlFor="ba-plz" className="text-xs uppercase tracking-wider text-muted-foreground">PLZ</Label>
              <Input id="ba-plz" value={form.postal_code} onChange={set("postal_code")} className="mt-1 font-mono" data-testid="ba-postal-code" />
            </div>
            <div>
              <Label htmlFor="ba-city" className="text-xs uppercase tracking-wider text-muted-foreground">Ort</Label>
              <Input id="ba-city" value={form.city} onChange={set("city")} className="mt-1" data-testid="ba-city" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Land</Label>
            <Select value={form.country} onValueChange={(v) => setForm((s) => ({ ...s, country: v }))}>
              <SelectTrigger className="mt-1" data-testid="ba-country"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground pt-2 leading-relaxed">
            Deine Adresse erscheint auf der Rechnung. Rechnungen enthalten keine Umsatzsteuer
            (§ 19 UStG, Kleinunternehmerregelung). Kundennummer und Rechnungsnummer werden
            automatisch vergeben.
          </p>
          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
            <Button type="submit" className="rounded-full" disabled={saving} data-testid="ba-save">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Speichert…</> : <><Save className="mr-2 h-4 w-4" strokeWidth={1.5} /> Speichern & weiter</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
